/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ChangeEvent, useEffect, useState } from "react";
import { useFormContext, useController } from "react-hook-form";
import * as yup from "yup";
import { FiEye, FiEyeOff } from "react-icons/fi";

interface InputProps {
  name: string;
  label: string;
  type?: string;
  validationSchema?: yup.Schema<any>;
  placeholder?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  value?: string | number;
  required?: boolean;
  disabled?: boolean;
  min?: any;
  max?: any;
  className?: string;
  disableAutoCapitalize?: boolean;
  onInput?: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  compact?: boolean;
  /** Visible row count for `type="textarea"` (default 4). */
  rows?: number;
  /**
   * Optional control rendered at the right end of the label row (e.g. a unit
   * toggle). NOTE: added so callers no longer need to absolutely position
   * controls over the label — that was hiding long label text.
   */
  labelAction?: React.ReactNode;
  /**
   * Reserve room for N lines of label text on this field only. Prefer
   * `reserveLabelLines()` on the surrounding grid so a whole row is consistent.
   */
  labelLines?: number;
}

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getWrapperClass = (compact: boolean) => (compact ? "mb-0" : "mb-2");

/** Height of one label line (text-sm + leading-snug), used to reserve rows. */
const LABEL_LINE_HEIGHT_REM = 1.25;

/**
 * CSS variable read by every Input's label row. Because custom properties
 * inherit, a form section can set it once on its grid and every field inside
 * (nested ones included) reserves the same label height.
 */
export const LABEL_ROW_MIN_H_VAR = "--label-row-min-h";

/**
 * NOTE: fixes label/input misalignment. In a dense grid a long label such as
 * "Annual Bonus (% of Basic)" wraps to two lines and used to push its own input
 * down, breaking the row. Reserving the same label height for every field in
 * the grid keeps all inputs on one vertical line, wrapped or not.
 *
 * Usage: <div className="grid …" style={reserveLabelLines(2)}> … </div>
 * Forms that do not opt in are completely unaffected (fallback: 0px).
 */
export const reserveLabelLines = (lines = 2): React.CSSProperties =>
  ({ [LABEL_ROW_MIN_H_VAR]: `${lines * LABEL_LINE_HEIGHT_REM}rem` } as React.CSSProperties);

/**
 * Label row: the label keeps the space it needs and any `labelAction` sits
 * beside it instead of on top of it. Spacing below is unchanged.
 * items-start + the reserved min-height mean labels always start at the same
 * y position and the field always sits at the bottom of the reserved box.
 */
const getLabelRowClass = (compact: boolean) =>
  `flex items-start justify-between gap-2 ${compact ? "mb-1" : "mb-2"}`;

/** Reads the inherited variable; 0px keeps non-opted-in forms unchanged. */
const LABEL_ROW_STYLE: React.CSSProperties = {
  minHeight: `var(${LABEL_ROW_MIN_H_VAR}, 0px)`,
};

// min-w-0 + break-words let long labels wrap instead of being clipped/covered
const LABEL_CLASS =
  "block min-w-0 text-sm font-medium text-gray-900 dark:text-white leading-snug break-words";

const SHARED_INPUT_AUTOFILL_STYLE_ID = "shared-input-autofill-style";
const SHARED_INPUT_AUTOFILL_STYLES = `
  .shared-input-field:-webkit-autofill,
  .shared-input-field:-webkit-autofill:hover,
  .shared-input-field:-webkit-autofill:focus,
  .shared-input-field:autofill,
  .shared-input-field:autofill:hover,
  .shared-input-field:autofill:focus {
    -webkit-text-fill-color: #111827 !important;
    caret-color: #111827;
    -webkit-box-shadow: 0 0 0 1000px #f9fafb inset !important;
    box-shadow: 0 0 0 1000px #f9fafb inset !important;
    background-color: #f9fafb !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  .dark .shared-input-field:-webkit-autofill,
  .dark .shared-input-field:-webkit-autofill:hover,
  .dark .shared-input-field:-webkit-autofill:focus,
  .dark .shared-input-field:autofill,
  .dark .shared-input-field:autofill:hover,
  .dark .shared-input-field:autofill:focus {
    -webkit-text-fill-color: #ffffff !important;
    caret-color: #ffffff;
    -webkit-box-shadow: 0 0 0 1000px #374151 inset !important;
    box-shadow: 0 0 0 1000px #374151 inset !important;
    background-color: #374151 !important;
  }
`;

const Input = ({
  name,
  label,
  type = "text",
  validationSchema,
  placeholder,
  onChange,
  value,
  disabled = false,
  required,
  min,
  max,
  compact = false,
  rows = 4,
  labelAction,
  labelLines,
  ...rest
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const resolvedType = type === "password" ? (showPassword ? "text" : "password") : type;

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(SHARED_INPUT_AUTOFILL_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = SHARED_INPUT_AUTOFILL_STYLE_ID;
    style.textContent = SHARED_INPUT_AUTOFILL_STYLES;
    document.head.appendChild(style);
  }, []);

  const {
    control,
  } = useFormContext();

  const {
    field,
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    defaultValue: "",
    rules: {
      validate: validationSchema
        ? async (value: any) => {
          try {
            await validationSchema.validate(value);
            return true;
          } catch (error: any) {
            return error.message;
          }
        }
        : undefined,
    },
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (
      e.target instanceof HTMLInputElement &&
      ["date", "number", "email", "password", "time", "datetime-local"].includes(
        e.target.type
      )
    ) {
      field.onChange(e);
      if (onChange) onChange(e);
      return;
    }

    let transformedValue = e.target.value.replace(
      /[^a-zA-Z0-9\s,.@+'()#-/]/g,
      ""
    );

    if (!rest.disableAutoCapitalize && transformedValue.length === 1) {
      transformedValue = capitalizeFirstLetter(transformedValue);
    }

    e.target.value = transformedValue;

    field.onChange(e);
    if (onChange) onChange(e);
  };

  const inputClass = `shared-input-field bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 
    dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 
    disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 
    dark:disabled:bg-gray-800 dark:disabled:text-gray-500
    ${type === "date" ? "dark:[color-scheme:dark]" : ""} 
    ${type === "password" ? "pr-10" : ""} 
    ${invalid
      ? "bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500"
      : "border-gray-300"
    }`;

  return (
    <div
      className={getWrapperClass(compact)}
      /* per-field override; the grid-level variable is used otherwise */
      style={labelLines ? reserveLabelLines(labelLines) : undefined}
    >
      <div className={getLabelRowClass(compact)} style={LABEL_ROW_STYLE}>
        <label htmlFor={name} className={LABEL_CLASS}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {/* e.g. the % / ₹ unit toggle — never overlaps the label */}
        {labelAction && <div className="shrink-0 flex items-center">{labelAction}</div>}
      </div>

      {type === "textarea" ? (
        <textarea
          {...field}
          id={name}
          rows={rows}
          placeholder={placeholder}
          className={inputClass}
          onChange={handleChange}
          value={value ?? field.value}
          {...rest}
        />
      ) : (
        <div className={type === "password" ? "relative" : ""}>
          <input
            {...field}
            // id matches the label's htmlFor so clicking the label focuses the field
            id={name}
            type={resolvedType}
            placeholder={placeholder}
            className={inputClass}
            onChange={handleChange}
            onInput={(e) => {
              if (rest.onInput) rest.onInput(e);
            }}
            onKeyDown={rest.onKeyDown}
            value={value ?? field.value}
            disabled={disabled ?? false}
            min={min}
            max={max}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            {...rest}
          />
          {type === "password" && (
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
            </button>
          )}
        </div>
      )}

      {/* compact: smaller error slot; default: original h-6 mt-1 preserved */}
      {/* <div className={getErrorSlotClass(compact)}>{renderErrorMessage(errMessage)}</div> */}
      <div className={`${compact ? "min-h-[1rem] mt-0.5" : "min-h-[1.5rem] mt-1"}`}>
        {error?.message ? (
          <p className="text-red-500 text-xs font-medium leading-tight">
            {error.message}
          </p>
        ) : (
          <p className="invisible text-xs font-medium leading-tight">placeholder</p>
        )}
      </div>
    </div>
  );
};

export default Input;